import { useContext, useEffect, useRef, useState } from "react";

import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { store } from "store/index";
import { selectCurrentUserId } from "store/selectors";
import { getCombinedConnectionStatus, isDisconnectedStatus } from "utils/connection-status";
import { logInfoWithDebugFlag } from "utils/logger";
import { getWindowId } from "utils/window";

// Delay before showing the banner so short disconnects don't flash UI.
const BANNER_SHOW_DELAY_MS = 3_000;
// After the banner is showing, wait this long before escalating "reconnecting" → "retrying".
const RETRYING_THRESHOLD_MS = 10_000;

export type ConnectionAlertStatus = "reconnecting" | "retrying" | "failed";

/**
 * Maps pubsub + ShareDB websocket health to a user-facing connection banner status
 * (consumed by `GlobalBanners`). Returns null when no banner should show.
 *
 * Transport status comes from `getCombinedConnectionStatus` / `isDisconnectedStatus`
 * in `utils/connection-status.ts` (also used by `ConnectionStatusIndicator`). That
 * value is the live socket state machine and can bounce on its own, e.g.:
 *
 *   disconnected → connecting → disconnected → … → connected | failed
 *
 * This hook's timers are separate from that state machine. They measure how long the
 * user has been in an outage for banner UX:
 *
 *   null          — healthy, or still inside the 3s pre-banner delay
 *   reconnecting  — banner visible, first ~10s
 *   retrying      — still down after that
 *   failed        — transport reports retries exhausted (reload required)
 *
 * When to clear the timers
 * ------------------------
 * Clear when the outage is over (`connected`) or terminal (`failed`), and on unmount
 * (leak prevention). Do NOT clear merely because `combinedStatus` changed to
 * `"connecting"`: that is a normal reconnect attempt, not recovery. Clearing then
 * would restart the 3s/10s clocks from zero every bounce, so the banner might never
 * appear or escalate. Covered by the "does not restart timers while already showing
 * reconnecting" test in `use-connection-alert-status-observer.test.tsx`.
 */
export const useConnectionAlertStatusObserver = (): ConnectionAlertStatus | null => {
  const shareDBContext = useContext(ShareDBContext);
  const { pubsubStatus } = useContext(PubsubWebsocketContext);
  const shareDBStatus = shareDBContext?.shareDBStatus ?? null;

  const combinedStatus = getCombinedConnectionStatus(shareDBStatus, pubsubStatus);

  const [alertStatus, setAlertStatus] = useState<ConnectionAlertStatus | null>(null);
  // Mirror in a ref so the effect can read the latest value without listing
  // `alertStatus` as a dependency (that would re-run the effect when the banner
  // appears and invite a mistaken timer cleanup).
  const alertStatusRef = useRef(alertStatus);
  useEffect(() => {
    alertStatusRef.current = alertStatus;
  }, [alertStatus]);
  const showTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const retryTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const resetTimers = () => {
    clearTimeout(showTimerRef.current);
    clearTimeout(retryTimerRef.current);
    showTimerRef.current = undefined;
  };

  // Intentionally no cleanup that clears timers on re-run.
  // `combinedStatus` changes on every reconnect bounce (e.g. to "connecting"). A
  // cleanup that clearTimeout'd here would wipe the clocks while the outage is
  // still ongoing; we only resetTimers() in the connected/failed branches below.
  // Unmount clearing is the empty-deps effect underneath.
  // react-doctor-disable-next-line react-doctor/effect-needs-cleanup
  useEffect(() => {
    if (combinedStatus === "connected") {
      resetTimers();
      setAlertStatus(null);
      return;
    }

    if (combinedStatus === "failed") {
      resetTimers();
      setAlertStatus("failed");
      return;
    }

    // Arm timers only once per outage: no show timer pending, banner still hidden.
    // Later "connecting" / "disconnected" bounces hit this effect again but skip
    // re-arming because alertStatusRef is already non-null (or a timer is pending).
    if (
      isDisconnectedStatus(combinedStatus) &&
      showTimerRef.current === undefined &&
      alertStatusRef.current === null
    ) {
      const userId = selectCurrentUserId(store.getState());
      const windowId = getWindowId();

      if (shareDBStatus !== null && isDisconnectedStatus(shareDBStatus)) {
        logInfoWithDebugFlag("debug_sharedb", "Shared DB Websocket disconnected", {
          userId,
          windowId,
          shareDBStatus,
        });
      }

      if (isDisconnectedStatus(pubsubStatus)) {
        logInfoWithDebugFlag("debug_pubsub", "Pubsub Websocket disconnected", {
          userId,
          windowId,
          pubsubStatus,
        });
      }

      // Show banner after BANNER_SHOW_DELAY_MS, then escalate after RETRYING_THRESHOLD_MS.
      // Retry is nested so its clock starts only once the banner is actually shown.
      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = undefined;
        setAlertStatus("reconnecting");
        retryTimerRef.current = setTimeout(
          () => setAlertStatus((status) => (status === "reconnecting" ? "retrying" : status)),
          RETRYING_THRESHOLD_MS,
        );
      }, BANNER_SHOW_DELAY_MS);
    }
  }, [combinedStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // clearTimeout on unmount only (avoid leaking if the component goes away mid-outage).
  useEffect(() => resetTimers, []);

  return alertStatus;
};
